/**
 * Exact decimal arithmetic for money entered by a user — Phase 06.
 *
 * WHY THIS EXISTS NEXT TO `lib/token-amount.ts`. That module formats amounts
 * that arrive from the backend as integer strings in a token's smallest unit.
 * This one handles the other direction: a decimal the USER typed ("0.0001"),
 * which has to be multiplied by a quantity, divided by a quantity, or reduced
 * by a fee to produce the numbers a transaction review shows. Neither module
 * lets a value pass through a float.
 *
 * Doing this in `Number` is not an option on this surface. `0.1 * 3` is
 * `0.30000000000000004`, and a review panel that shows a seller a total which
 * is not the total is the exact harm the transactional standard exists to
 * prevent. Everything here is BigInt over a scaled integer, so every result is
 * either exact or explicitly flagged as truncated.
 *
 * PERSIAN DIGITS. `normalizeDigits` accepts Persian (۰-۹) and Arabic-Indic
 * (٠-٩) digits plus the Persian decimal separator (٫) and the Arabic thousands
 * mark, because a Persian-locale keyboard produces them and `Number()` returns
 * NaN for every one. Normalisation happens on parse, never on the stored input
 * value — the user keeps seeing what they typed.
 */

export type Decimal = {
  negative: boolean;
  /** Unscaled value: the digits with the point removed. */
  units: bigint;
  /** Number of digits to the right of the point. */
  scale: number;
};

const PERSIAN_ZERO = 0x06f0;
const ARABIC_ZERO = 0x0660;

/** Persian/Arabic-Indic digits and separators → ASCII. */
export function normalizeDigits(raw: string): string {
  let out = "";
  for (const char of raw) {
    const code = char.codePointAt(0)!;
    if (code >= PERSIAN_ZERO && code <= PERSIAN_ZERO + 9) {
      out += String(code - PERSIAN_ZERO);
    } else if (code >= ARABIC_ZERO && code <= ARABIC_ZERO + 9) {
      out += String(code - ARABIC_ZERO);
    } else if (char === "٫" || char === "،") {
      // Persian decimal separator, and the Persian comma some keyboards emit
      // in its place.
      out += ".";
    } else if (char === "٬" || char === ",") {
      // Thousands separators are dropped, not translated to a point.
      continue;
    } else {
      out += char;
    }
  }
  return out;
}

/**
 * Parse a user-entered decimal. Returns null for anything that is not a plain
 * decimal number — including scientific notation, which is deliberate: it
 * arrives from the backend on `price_per_item` but a user never types it, and
 * accepting it here would mean guessing at intent.
 */
export function parseDecimal(raw: string | number | null | undefined): Decimal | null {
  if (raw === null || raw === undefined) return null;
  const text = normalizeDigits(String(raw)).trim();
  if (!text) return null;

  const match = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(text);
  if (!match) return null;

  const [, sign, wholeRaw, fractionRaw = ""] = match;
  if (!wholeRaw && !fractionRaw) return null;

  const digits = `${wholeRaw || "0"}${fractionRaw}`;
  const units = BigInt(digits);
  return {
    negative: sign === "-" && units !== BigInt(0),
    units,
    scale: fractionRaw.length,
  };
}

/**
 * A whole-number input, with Persian and Arabic-Indic digits accepted. Returns
 * null rather than 0 for anything unusable, so a bad quantity blocks a submit
 * instead of silently becoming zero.
 */
export function parseIntegerInput(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  const text = normalizeDigits(String(raw)).trim();
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return Number.isSafeInteger(value) ? value : null;
}

/**
 * Backend-supplied amounts, which may arrive in scientific notation — the
 * single-NFT `listing.price_per_item` is documented as sometimes being
 * `"1.0E-14"`. `parseDecimal` deliberately refuses that form (a user never
 * types it, and accepting it there would mean guessing at intent), so the
 * exponent is expanded here first and the tolerance is preserved.
 *
 * `String(1e-14)` is `"1e-14"`, so a number that has already been through
 * `Number()` needs this too.
 */
export function parseApiAmount(raw: string | number | null | undefined): Decimal | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number" && !Number.isFinite(raw)) return null;

  const text = normalizeDigits(String(raw)).trim();
  const scientific = /^([+-]?)(\d+)(?:\.(\d*))?[eE]([+-]?\d+)$/.exec(text);
  if (!scientific) return parseDecimal(text);

  const [, sign, whole, fraction = "", exponentRaw] = scientific;
  const exponent = Number(exponentRaw);
  const digits = `${whole}${fraction}`;
  // The point currently sits after `whole`; the exponent moves it.
  const pointAt = whole.length + exponent;

  let body: string;
  if (pointAt <= 0) {
    body = `0.${"0".repeat(-pointAt)}${digits}`;
  } else if (pointAt >= digits.length) {
    body = `${digits}${"0".repeat(pointAt - digits.length)}`;
  } else {
    body = `${digits.slice(0, pointAt)}.${digits.slice(pointAt)}`;
  }
  return parseDecimal(`${sign}${body}`);
}

/** Canonical decimal string. Trailing fraction zeros are trimmed. */
export function formatDecimal(value: Decimal): string {
  const digits = value.units.toString().padStart(value.scale + 1, "0");
  const cut = digits.length - value.scale;
  const whole = digits.slice(0, cut);
  const fraction = value.scale > 0 ? digits.slice(cut).replace(/0+$/, "") : "";
  const body = fraction ? `${whole}.${fraction}` : whole;
  return value.negative ? `-${body}` : body;
}

export function isPositive(value: Decimal): boolean {
  return !value.negative && value.units > BigInt(0);
}

export function isZero(value: Decimal): boolean {
  return value.units === BigInt(0);
}

function signedUnits(value: Decimal): bigint {
  return value.negative ? -value.units : value.units;
}

/** Re-express both operands at the same scale so they can be compared or added. */
function align(a: Decimal, b: Decimal): { left: bigint; right: bigint; scale: number } {
  const scale = Math.max(a.scale, b.scale);
  const lift = (v: Decimal) => signedUnits(v) * BigInt(10) ** BigInt(scale - v.scale);
  return { left: lift(a), right: lift(b), scale };
}

function fromSigned(units: bigint, scale: number): Decimal {
  return { negative: units < BigInt(0), units: units < BigInt(0) ? -units : units, scale };
}

export function addDecimal(a: Decimal, b: Decimal): Decimal {
  const { left, right, scale } = align(a, b);
  return fromSigned(left + right, scale);
}

export function subtractDecimal(a: Decimal, b: Decimal): Decimal {
  const { left, right, scale } = align(a, b);
  return fromSigned(left - right, scale);
}

/** -1, 0 or 1. Exact — no float ever ties two long amounts incorrectly. */
export function compareDecimal(a: Decimal, b: Decimal): number {
  const { left, right } = align(a, b);
  return left < right ? -1 : left > right ? 1 : 0;
}

/**
 * Multiply by a whole quantity. Always exact: multiplying a scaled integer by
 * an integer cannot need more digits than it already has.
 */
export function multiplyByInteger(value: Decimal, factor: number): Decimal | null {
  if (!Number.isInteger(factor) || !Number.isFinite(factor)) return null;
  const units = signedUnits(value) * BigInt(factor);
  return fromSigned(units, value.scale);
}

/**
 * Divide by a whole quantity to `maxScale` digits. `exact` is false when the
 * result was truncated, so a caller can label a derived per-item price as
 * rounded rather than presenting a truncated number as the real one.
 */
export function divideByInteger(
  value: Decimal,
  divisor: number,
  maxScale = 18,
): { value: Decimal; exact: boolean } | null {
  if (!Number.isInteger(divisor) || !Number.isFinite(divisor) || divisor === 0) return null;

  const lift = BigInt(10) ** BigInt(Math.max(0, maxScale - value.scale));
  const numerator = signedUnits(value) * lift;
  const denominator = BigInt(divisor);
  const quotient = numerator / denominator;
  const exact = quotient * denominator === numerator;
  return { value: fromSigned(quotient, Math.max(value.scale, maxScale)), exact };
}

/**
 * Convenience wrappers over the string form, for call sites that only ever
 * handle display strings. Each returns null when any input is unusable, so a
 * missing value can never collapse into `0`.
 */
export function multiplyDecimalString(text: string, factor: number): string | null {
  const parsed = parseDecimal(text);
  if (!parsed) return null;
  const result = multiplyByInteger(parsed, factor);
  return result ? formatDecimal(result) : null;
}

export function divideDecimalString(
  text: string,
  divisor: number,
  maxScale = 18,
): { text: string; exact: boolean } | null {
  const parsed = parseDecimal(text);
  if (!parsed) return null;
  const result = divideByInteger(parsed, divisor, maxScale);
  return result ? { text: formatDecimal(result.value), exact: result.exact } : null;
}

/**
 * Net = gross − each deduction, but ONLY when every deduction is a real value.
 * A single null makes the whole result null: a net figure computed from an
 * unknown fee is a plausible-but-wrong number on a seller's screen, which is
 * worse than no number at all (D010).
 */
export function netOf(gross: string | null, deductions: (string | null)[]): string | null {
  const grossValue = gross === null ? null : parseDecimal(gross);
  if (!grossValue) return null;

  let running = grossValue;
  for (const deduction of deductions) {
    if (deduction === null) return null;
    const parsed = parseDecimal(deduction);
    if (!parsed) return null;
    running = subtractDecimal(running, parsed);
  }
  return formatDecimal(running);
}
